import { useEffect, useState } from 'react';
import { api, ago, humanise, type JobRun } from '../api';
import { PageHead, Panel, State, Table, Toast, useLoad } from '../ui';

/**
 * Operational settings, changed here without a deploy, and the record of the
 * scheduled planning runs (docs/15-RIDE-FLOW.md, phases 3 and 4).
 */
export function Settings() {
  const settings = useLoad(() => api.settings());
  const jobs = useLoad(() => api.jobs(20));
  const [wait, setWait] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; bad?: boolean } | null>(null);

  const def = settings.data?.definitions.pickupWaitMin;
  const current = settings.data?.values.pickupWaitMin;

  useEffect(() => {
    if (current !== undefined) setWait(String(current));
  }, [current]);

  const n = Number(wait);
  const valid = def !== undefined && Number.isInteger(n) && n >= def.min && n <= def.max;
  const changed = current !== undefined && n !== current;

  const save = async () => {
    setSaving(true);
    try {
      await api.saveSettings({ pickupWaitMin: n });
      setToast({ msg: `Drivers now wait ${n} minute${n === 1 ? '' : 's'} at each pickup.` });
      await settings.reload();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Could not save', bad: true });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  return (
    <>
      <PageHead
        title="Settings"
        blurb="Changes apply to the next pickup — no deploy, and every change is recorded in the audit log."
      />

      <Panel title="Pickups">
        {settings.error ? (
          <div className="empty">{settings.error}</div>
        ) : !def ? (
          <div className="empty">Loading…</div>
        ) : (
          <form
            className="setting"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid && changed) void save();
            }}
          >
            <label htmlFor="wait">
              <strong>{def.label}</strong>
              <span>
                After this long at a pickup with no QR scan or code, the rider is marked a no-show and the
                driver moves on. {def.min}–{def.max} minutes; default {def.default}.
              </span>
            </label>
            <div className="row">
              <input
                id="wait"
                type="number"
                min={def.min}
                max={def.max}
                step={1}
                value={wait}
                onChange={(e) => setWait(e.target.value)}
                aria-invalid={!valid}
              />
              <button className="btn primary" type="submit" disabled={!valid || !changed || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {!valid && wait !== '' ? (
              <p className="hint bad">Enter a whole number from {def.min} to {def.max}.</p>
            ) : null}
          </form>
        )}
      </Panel>

      <Panel
        title="Scheduled planning"
        right={
          <button className="btn sm" onClick={() => void jobs.reload()} disabled={jobs.loading}>
            Refresh
          </button>
        }
      >
        {jobs.data && !jobs.data.enabled ? (
          <p className="hint">
            The scheduler is off in this environment. In production it plans mornings at 21:00 and evenings at
            14:00 IST.
          </p>
        ) : null}
        {jobs.error ? (
          <div className="empty">{jobs.error}</div>
        ) : (
          <Table<JobRun>
            rows={jobs.data?.runs ?? null}
            keyOf={(r) => r.id}
            empty="No scheduled runs yet."
            columns={[
              { head: 'Job', cell: (r) => humanise(r.job) },
              { head: 'For', shrink: true, cell: (r) => r.run_key },
              { head: 'Started', shrink: true, cell: (r) => ago(r.started_at) },
              {
                head: 'Result',
                shrink: true,
                cell: (r) =>
                  r.ok === null ? (
                    <State tone="live">Running</State>
                  ) : r.ok ? (
                    <State tone="ok">Done</State>
                  ) : (
                    <State tone="bad">Failed</State>
                  ),
              },
              {
                head: 'Detail',
                cell: (r) =>
                  r.ok === false
                    ? String(r.detail?.error ?? '')
                    : r.detail && 'matched' in r.detail
                      ? `${r.detail.matched}/${r.detail.riders} matched · ${r.detail.chosenKept ?? 0} chose their ride`
                      : '',
              },
            ]}
          />
        )}
      </Panel>

      {toast ? <Toast msg={toast.msg} bad={toast.bad} /> : null}
    </>
  );
}
